import { useEffect, useRef } from "react";
import { useAppData, useConfigGuard } from "../contexts/AppContext";
import { canAccessKanbanV2 } from "../routes/routeAccess";
import { readSessionToken } from "../_services/api";
import { checkCollaboratorPermission } from "../pages/Users/utils/checkPermission";

/**
 * GEN-4225 — cronômetro flutuante do Kanban 2.0 em TODAS as telas da área autenticada.
 *
 * O cronômetro em andamento de quem está logado precisa ficar à vista onde a pessoa estiver (decisão do
 * Thiago na revisão dos mockups): por isso ele não mora dentro do Kanban, e sim aqui, no layout. A lib monta
 * o componente num React root próprio (`@taxlybr/kanban2-web/cronometro`, pacote pequeno — não baixa o
 * Kanban inteiro). Sem cronômetro em andamento ele não desenha nada.
 *
 * Só para quem abre o Kanban 2.0: produto `kanban2` no cliente E permissão `kanban2.read` — a MESMA régua da
 * rota `/kanban-v2` (App.tsx). Sem a permissão, o relógio consultaria a API a cada minuto e levaria 403 sempre
 * (erro na rota `/kanban2/time/active` relatado em HMLG em 29/10). Remonta ao trocar de cliente.
 */
export function CronometroGlobal() {
  const appData = useAppData();
  const guidClient = appData.guid_client;
  const liberado =
    !!guidClient &&
    canAccessKanbanV2(appData) &&
    checkCollaboratorPermission(appData.validation, guidClient, "kanban2.read");
  const containerRef = useRef<HTMLDivElement>(null);
  // Codex (corte [MAIN]): "Abrir" passa pelo mesmo guarda da sidebar — com alteração não salva nas
  // Configurações, pergunta antes de sair em vez de descartar a edição.
  const { requestNavigation } = useConfigGuard();
  const navegarRef = useRef(requestNavigation);
  navegarRef.current = requestNavigation;

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !guidClient || !liberado) return undefined;
    let unmount: (() => void) | undefined;
    let cancelado = false;
    Promise.all([import("@taxlybr/kanban2-web/cronometro"), import("@taxlybr/kanban2-web/style.css")])
      .then(([{ mountCronometro }]) => {
        if (cancelado || !containerRef.current) return;
        unmount = mountCronometro(el, {
          clientId: guidClient,
          apiBase: `${process.env.REACT_APP_TAXLY_API}/kanban2`,
          getToken: readSessionToken,
          // O host navega (pelo guarda de alteração não salva); com o Kanban já aberto, o próprio quadro
          // abre o card e este callback nem é chamado.
          onOpenActivity: ({ boardId, cardId }) =>
            navegarRef.current(
              `/kanban-v2/quadros/${encodeURIComponent(boardId)}/cards/${encodeURIComponent(cardId)}`
            ),
        });
      })
      .catch(() => {
        /* lib antiga sem o cronômetro, ou chunk que não carregou: a tela segue sem ele */
      });
    return () => {
      cancelado = true;
      unmount?.();
    };
  }, [guidClient, liberado]);

  return <div ref={containerRef} data-k2-cronometro="" />;
}
